// @ts-check
/**
 * TODO TODO TODO TODO TODO TODO TODO TODO TODO TODO TODO TODO TODO TODO
 * This component must be urgently refactored and all unnecessary code removed.
 */
import { gql, useMutation, useQuery } from '@apollo/client';
import { omit } from 'lodash';
import React from 'react';
import Notification from '../Notification';
import FieldsTable from './FieldsTable';
import getBottlebooksFieldsMap from './getBottlebooksFieldsMap';

const EMPTY = '(empty)';

/**
 * Renders the page where fields are mapped
 * Bottlebooks fields are rendered and all columns from import are available in the dropdown
 */
export default function MapIdFields({
  loading,
  fieldsToImport,
  notification,
  cancelNotification,
  bottlebooksFieldsMap,
  sourceFields,
  handleFieldsTableChange,
  ...rest
}: {
  loading: boolean;
  fieldsToImport: FieldsToImport;
  notification: Notification | undefined;
  cancelNotification: () => void;
  bottlebooksFieldsMap: any;
  sourceFields: { name: string; sampleRows: string[] }[];
  handleFieldsTableChange: (
    value: any,
    col: any,
    originalBottlebooksFieldsMap: any
  ) => void;
} & {
  latestBottlebooksFields: any; //  - the previous fields mapped to be used on new imports
}) {
  if (loading) return null;

  return (
    <React.Fragment>
      <FieldsTable
        sx={{ width: '100%', margin: '0 auto', marginBottom: 5, marginTop: 2 }}
        bottlebooksFieldsMap={bottlebooksFieldsMap}
        sourceFields={sourceFields}
        fieldsToImport={fieldsToImport}
        onChange={(value, col) =>
          handleFieldsTableChange(value, col, bottlebooksFieldsMap)
        }
        {...rest}
      />

      {notification && (
        <Notification
          onClose={() => cancelNotification()}
          message={notification.message}
          variant={notification.success ? 'success' : 'error'}
        />
      )}
    </React.Fragment>
  );
}

type FieldsToImport = Array<{
  key: string;
  label: string;
  required: boolean;
}>;

export function useIdentifierFields({
  workspaceId,
  importId,
  fieldsToImport,
}: {
  workspaceId: string;
  importId: string;
  fieldsToImport: FieldsToImport;
}) {
  const [_updateIdentifierMapping] = useMutation(UPDATE_IDENTIFIER_MAPPING);
  const { loading, /* error, */ data /*, refetch */ } = useQuery(
    IDENTIFY_FIELDS_QUERY,
    {
      variables: {
        workspaceId,
        importId,
      },
    }
  );

  // TODO we don't need all the imports. Add filter and limit latest 10 or 20
  const bottlebooksFieldsNodes = data?.workspace?.dataImports?.nodes?.filter(
    ({ identifierFields }) => identifierFields
  );
  const latestBottlebooksFields = bottlebooksFieldsNodes?.reduce(
    (acc, { identifierFields }) => {
      acc = [...identifierFields, ...acc];
      return acc;
    },
    []
  );

  const commonProps = useFieldsCommon({
    importId,
    fieldsToImport,
    data,
    latestBottlebooksFields,
  });
  const {
    setNotification,
    bottlebooksFieldsMap,
    isSubmitting,
    setIsSubmitting,
    originalBottlebooksFieldsMap,
  } = commonProps;

  return {
    loading,
    updateIdentifierMapping,
    identifierFields: data?.workspace?.dataImport?.identifierFields,
    ...commonProps,
  };

  /**
   * Updates the identifier fields mapped
   */
  async function updateIdentifierMapping() {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const { data } = await _updateIdentifierMapping({
        variables: {
          input: {
            importId,
            identifierFields: Object.values(
              bottlebooksFieldsMap || originalBottlebooksFieldsMap
            ),
          },
        },
      });
      if (!data?.result?.success) {
        setNotification({
          message: 'There was an error, please try again!',
        });
      }
      setIsSubmitting(false);
    } catch (err) {
      setNotification({
        message: 'There was an error, please try again!',
      });
      setIsSubmitting(false);
      return { error: err };
    }
  }
}
export function useMapIdFields({ workspaceId, importId, fieldsToImport }) {
  const { loading, /* error, */ data /*, refetch */ } = useQuery(
    MAP_FIELDS_QUERY,
    {
      variables: {
        workspaceId,
        importId,
      },
    }
  );

  // TODO we don't need all the imports. Add filter and limit latest 10 or 20
  const bottlebooksFieldsNodes = data?.workspace?.dataImports?.nodes?.filter(
    ({ bottlebooksFields }) => bottlebooksFields
  );
  const latestBottlebooksFields = bottlebooksFieldsNodes?.reduce(
    (acc, { bottlebooksFields }) => {
      acc = [...bottlebooksFields, ...acc];
      return acc;
    },
    []
  );

  const commonProps = useFieldsCommon({
    importId,
    fieldsToImport,
    data,
    latestBottlebooksFields,
  });
  const {
    setNotification,
    bottlebooksFieldsMap,
    isSubmitting,
    setIsSubmitting,
    originalBottlebooksFieldsMap,
  } = commonProps;

  const [_updateColumnsMapping] = useMutation(UPDATE_COLUMNS_MAPPING);

  /**
   * Updates the bottlebooks fields mapped
   */
  async function updateColumnsMapping() {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const { data } = await _updateColumnsMapping({
        variables: {
          input: {
            importId,
            bottlebooksFields: Object.values(
              bottlebooksFieldsMap || originalBottlebooksFieldsMap
            ),
          },
        },
      });
      if (!data?.result?.success) {
        setNotification({
          message: 'There was an error, please try again!',
        });
      }
      setIsSubmitting(false);
    } catch (err) {
      setNotification({
        message: 'There was an error, please try again!',
      });
      setIsSubmitting(false);
      return { error: err };
    }
  }

  return {
    loading,
    importId,
    fieldsToImport,
    updateColumnsMapping,
    ...commonProps,
  };
}

function useFieldsCommon({
  importId,
  fieldsToImport,
  data,
  latestBottlebooksFields,
}: {
  importId: string;
  fieldsToImport: FieldsToImport;
  data: any;
  latestBottlebooksFields: any;
}) {
  const afterQueryData = afterQuery({
    data,
    fieldsToImport,
    latestBottlebooksFields,
  });

  /** @type {[import('../Notification').Notification | undefined, React.Dispatch<React.SetStateAction<import('../Notification').Notification | undefined>>]} */
  const [notification, setNotification] = React.useState();
  /** @type {[boolean, (boolean)=>void]} */
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [bottlebooksFieldsMap, setBottlebooksFieldsMap] = React.useState(
    afterQueryData.originalBottlebooksFieldsMap
  );

  function areAllRequiredFieldsFilled(bottlebooksFieldsMap) {
    if (!bottlebooksFieldsMap) return null;
    return !fieldsToImport.find((col) => {
      return (
        col.required &&
        (!bottlebooksFieldsMap[col.key] ||
          bottlebooksFieldsMap[col.key] === EMPTY)
      );
    });
  }

  async function handleFieldsTableChange(
    value,
    col,
    originalBottlebooksFieldsMap
  ) {
    let newMapping = {};
    const bottlebooksFieldsMapSrc = bottlebooksFieldsMap
      ? bottlebooksFieldsMap
      : originalBottlebooksFieldsMap;
    if (value === undefined || value === EMPTY) {
      newMapping = omit(bottlebooksFieldsMapSrc, col.key);
      setBottlebooksFieldsMap(newMapping);
    } else {
      newMapping = {
        ...bottlebooksFieldsMapSrc,
        [col.key]: {
          importFieldName: value,
          bottlebooksFieldName: col.key,
        },
      };
      setBottlebooksFieldsMap(newMapping);
    }
  }

  return {
    importId,
    ...afterQueryData,
    isSubmitting,
    setIsSubmitting,
    fieldsToImport,
    isValid:
      areAllRequiredFieldsFilled(
        bottlebooksFieldsMap || afterQueryData.originalBottlebooksFieldsMap
      ) && !isSubmitting,
    notification,
    setNotification,
    cancelNotification: () => {
      setNotification(undefined);
    },
    bottlebooksFieldsMap:
      bottlebooksFieldsMap || afterQueryData.originalBottlebooksFieldsMap,
    handleFieldsTableChange,
  };
}

function afterQuery({ data, fieldsToImport, latestBottlebooksFields }) {
  if (!data) return {};

  const dataImport = data?.workspace?.dataImport;

  const { source, bottlebooksFields } = dataImport;
  const sourceFieldNames = source?.fields?.map(({ name }) => name);
  const filteredLatestBottlebooksFields = latestBottlebooksFields?.filter(
    ({ importFieldName }) => sourceFieldNames?.includes(importFieldName)
  );

  //The columns automatically matched or matched by the user
  const originalBottlebooksFieldsMap = getBottlebooksFieldsMap({
    bottlebooksFields,
    latestBottlebooksFields: filteredLatestBottlebooksFields,
    sourceFields: source?.fields,
    fieldsToImport,
  });

  return {
    latestBottlebooksFields,
    originalBottlebooksFieldsMap,
    sourceFieldNames,
    sourceFields:
      source?.fields.map(({ name, rawTexts }) => ({
        name,
        sampleRows: rawTexts,
      })) ?? [],
  };
}

MapIdFields.mapFieldsSourceFragment = gql`
  fragment MapIdFieldsSource on DataImport {
    source {
      fields {
        name
        bottlebooksFieldName
        rawTexts(first: 3)
      }
    }
  }
`;

MapIdFields.mapFieldsFragment = gql`
  fragment MapIdFields on DataImport {
    importId
    bottlebooksFields {
      bottlebooksFieldName
      importFieldName
    }
  }
`;

MapIdFields.identifierFieldsFragment = gql`
  fragment IdentifierFields on DataImport {
    importId
    identifierFields {
      bottlebooksFieldName
      importFieldName
    }
  }
`;

const MAP_FIELDS_QUERY = gql`
  query MapIdFieldsPage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        ...MapIdFields
        ...MapIdFieldsSource
      }
      dataImports {
        nodes {
          ...MapIdFields
        }
      }
    }
  }
  ${MapIdFields.mapFieldsFragment}
  ${MapIdFields.mapFieldsSourceFragment}
`;
const IDENTIFY_FIELDS_QUERY = gql`
  query IdentifierFieldsPage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        ...IdentifierFields
        ...MapIdFieldsSource
      }
      dataImports {
        nodes {
          ...IdentifierFields
        }
      }
    }
  }
  ${MapIdFields.identifierFieldsFragment}
  ${MapIdFields.mapFieldsSourceFragment}
`;

export const UPDATE_COLUMNS_MAPPING = gql`
  mutation updateDataImportBottlebooksFields(
    $input: UpdateDataImportBottlebooksFieldsInput!
  ) {
    result: updateDataImportBottlebooksFields(input: $input) {
      code
      success
      message
      dataImport {
        ...MapIdFields
        ...MapIdFieldsSource
      }
    }
  }
  ${MapIdFields.mapFieldsFragment}
  ${MapIdFields.mapFieldsSourceFragment}
`;
export const UPDATE_IDENTIFIER_MAPPING = gql`
  mutation updateDataImportIdentifierFields(
    $input: updateDataImportIdentifierFieldsInput!
  ) {
    result: updateDataImportIdentifierFields(input: $input) {
      code
      success
      message
      dataImport {
        ...IdentifierFields
        ...MapIdFieldsSource
      }
    }
  }
  ${MapIdFields.mapFieldsSourceFragment}
  ${MapIdFields.identifierFieldsFragment}
`;
